import { useState } from 'react';

import { useFormik } from 'formik';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

import Input from 'components/Input/Input';
import { newPackageFormSchema } from './utils';
import { Progress } from 'components/ui/progress';
import RichText from 'components/RichText/RichText';
import { VersionStatus, Version } from 'common/contracts';
import FormHeader from 'components/FormHeader/FormHeader';
import { showToast, EToasterStatus } from 'utils/showToast';
import useDistributionManager from 'api/useDistributionManager';
import { UploadFileButton } from 'components/UploadFileButton/UploadFileButton';
import { UploadFileWrapper } from 'components/UploadFileButton/UploadFileWrapper';

const PackageExplorerForm = () => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const navigate = useNavigate();
  const { createVersion, getUploadUrl, uploadFileToSignedUrl } =
    useDistributionManager();

  const {
    values,
    handleChange,
    handleBlur,
    submitForm,
    isValid,
    errors,
    touched
  } = useFormik({
    validateOnMount: true,
    validationSchema: newPackageFormSchema,
    initialValues: {
      versionName: '',
      versionCode: '',
      packageName: '',
      updateType: '',
      errorRedirectUrl: ''
    },
    onSubmit: async (values) => {
      const newVersion: Partial<Version> = {
        versionName: values.versionName,
        versionCode: values.versionCode,
        packageName: values.packageName,
        status: VersionStatus.TESTING,
        releaseConfiguration: {
          message: 'DEFAULT',
          fallbackUrl: values.errorRedirectUrl,
          fallbackMessage: 'DEFAULT'
        }
      };
      createVersion.mutate(newVersion, {
        onSuccess: () => {
          onVersionCreated(newVersion);
        },
        onError: () => {
          showToast({
            message: 'Error creating version',
            status: EToasterStatus.ERROR
          });
        }
      });
    }
  });

  const onVersionCreated = async (newVersion: Partial<Version>) => {
    try {
      const uploadUrl = await getUploadUrl(
        newVersion.versionCode || '',
        newVersion.packageName || ''
      );
      uploadFileToSignedUrl.mutate(
        {
          file: selectedFile!,
          signedUrl: uploadUrl,
          onUploadProgress: (progress) => {
            const percentage = Math.round(
              (progress.loaded * 100) / progress.total
            );
            setUploadProgress(percentage);
          }
        },
        {
          onSuccess: () => {
            showToast({
              message: 'File uploaded successfully',
              status: EToasterStatus.SUCCESS
            });
            navigate('../');
          },
          onError: () => {
            showToast({
              message: 'Error uploading file',
              status: EToasterStatus.ERROR
            });
          }
        }
      );
    } catch (error) {
      showToast({
        message: 'Error creating version',
        status: EToasterStatus.ERROR
      });
    }
  };

  const uploadFile = (files: File[] | null) => {
    const file = files?.[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 20 }}
      transition={{ duration: 0.3, ease: 'easeInOut' }}
      className="bg-white fixed h-screen w-full top-0 left-0 z-[1000] overflow-hidden"
    >
      <FormHeader
        onCloseForm={() => navigate('../')}
        onSubmitForm={submitForm}
        title="Create a new release"
        isFormValid={
          isValid &&
          !!selectedFile &&
          !createVersion.isLoading &&
          !createVersion.isSuccess
        }
        buttonText="Save"
        closeFormOnSubmit={false}
        showPreviewButton={false}
      />
      <div className="mt-10 w-full mx-auto overflow-y-auto h-[calc(100vh-160px)]">
        <div className="w-[456px] mx-auto">
          <RichText title="1. New Package" className="mb-4" />
          <div className="flex flex-col gap-5 mb-4">
            <div className="grid grid-cols-2 gap-5">
              <Input
                label="Version Code"
                id="versionCode"
                placeholder="Version code"
                type="number"
                name="versionCode"
                isRequired
                value={values.versionCode}
                onChange={handleChange}
                onBlur={handleBlur}
                state={
                  touched.versionCode && errors.versionCode
                    ? 'error'
                    : 'default'
                }
                description={
                  touched.versionCode && errors.versionCode
                    ? errors.versionCode
                    : ''
                }
              />
              <Input
                label="Version Name"
                id="versionName"
                placeholder="Version name"
                type="text"
                name="versionName"
                isRequired
                value={values.versionName}
                onChange={handleChange}
                onBlur={handleBlur}
                state={
                  touched.versionName && errors.versionName
                    ? 'error'
                    : 'default'
                }
                description={
                  touched.versionName && errors.versionName
                    ? errors.versionName
                    : ''
                }
              />
              <Input
                label="Package Name"
                placeholder="Package name"
                type="text"
                name="packageName"
                className="col-span-2"
                isRequired
                value={values.packageName}
                onChange={handleChange}
                onBlur={handleBlur}
                state={
                  touched.packageName && errors.packageName
                    ? 'error'
                    : 'default'
                }
                description={
                  touched.packageName && errors.packageName
                    ? errors.packageName
                    : ''
                }
              />
              <Input
                label="Error redirect URL"
                placeholder="URL"
                type="text"
                name="errorRedirectUrl"
                className="col-span-2"
                isRequired
                value={values.errorRedirectUrl}
                onChange={handleChange}
                onBlur={handleBlur}
                state={
                  touched.errorRedirectUrl && errors.errorRedirectUrl
                    ? 'error'
                    : 'default'
                }
                description={
                  touched.errorRedirectUrl && errors.errorRedirectUrl
                    ? errors.errorRedirectUrl
                    : ''
                }
              />
            </div>
          </div>

          <div className="my-5 border-t" />
          <div className={!isValid ? 'opacity-50' : ''}>
            <RichText title="2. Upload APK file" className="mb-4" />
            <UploadFileWrapper
              className="mb-4 mt-5"
              title={selectedFile ? selectedFile.name : 'Select file'}
            >
              <UploadFileButton
                buttonProps={{
                  disabled: !isValid || uploadFileToSignedUrl.isLoading,
                  isLoading: uploadFileToSignedUrl.isLoading
                }}
                onFileSelect={uploadFile}
                accept=".apk"
              />
            </UploadFileWrapper>
            <Progress value={uploadProgress} />
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default PackageExplorerForm;
